import { HotspotTypeMap } from '@/entities/site'
import type { HotspotType } from '@/entities/site'
import type { PerspectiveCamera } from 'three'

export interface SiteAnnotationState {
  zoomFactor: number
  aspect: number
  imageDimensions: Size | null
  camera: PerspectiveCamera | null
  uv: Point | null
}

function _makeDefaultState(): SiteAnnotationState {
  return {
    zoomFactor: 1,
    aspect: 1,
    imageDimensions: null,
    camera: null,
    uv: null,
  }
}

const HOTSPOT_TYPES: string[] = ['3', 'P', 'I', 'S']
const noop = () => {}

interface Options {
  onZoomChange?(val: number): void
  onAspectRatioChange?(val: number): void
  onImageDimensionChange?(val: Size): void
  onSiteCameraChange?(val: PerspectiveCamera | null): void
  onUvPanChange?(val: Point | null): void
}

interface UseSiteAnnotationState {
  updateThreeDAnnotations<K extends keyof SiteAnnotationState>(key: K, value: SiteAnnotationState[K]): void
  updateImageHotspotAnnotations<K extends keyof SiteAnnotationState>(key: K, value: SiteAnnotationState[K]): void
  updatePdfHotspotAnnotations<K extends keyof SiteAnnotationState>(key: K, value: SiteAnnotationState[K]): void
  updateSiteAnnotations<K extends keyof SiteAnnotationState>(key: K, value: SiteAnnotationState[K]): void
}

export const useSiteAnnotationState = (
  hotspotType: Readonly<Ref<HotspotType | null>>,
  {
    onZoomChange = noop,
    onAspectRatioChange = noop,
    onImageDimensionChange = noop,
    onSiteCameraChange = noop,
    onUvPanChange = noop,
  }: Options = {}
): UseSiteAnnotationState => {
  const getStateKey = (key: HotspotType | null) => (key && HOTSPOT_TYPES.includes(key) ? HotspotTypeMap[key] : 'site')
  const stateKey = computed(() => getStateKey(hotspotType.value))

  const state: Record<string, SiteAnnotationState> = {
    model: reactive(_makeDefaultState()),
    pdf: reactive(_makeDefaultState()),
    image: reactive(_makeDefaultState()),
    site: reactive(_makeDefaultState()),
  }

  watch(
    computed(() => state[stateKey.value].aspect),
    val => onAspectRatioChange(val)
  )
  watch(
    computed(() => state[stateKey.value].zoomFactor),
    val => {
      onZoomChange(val)
    }
  )
  watch(
    computed(() => state[stateKey.value].imageDimensions),
    val => val && onImageDimensionChange(val)
  )
  watch(
    computed(() => state[stateKey.value].camera),
    val => onSiteCameraChange(val)
  )
  watch(
    computed(() => state[stateKey.value].uv),
    val => onUvPanChange(val)
  )

  onDeactivated(() => {
    onSiteCameraChange(null)
    onUvPanChange(null)
    onZoomChange(1)
  })

  onActivated(() => {
    onSiteCameraChange(state[stateKey.value].camera)
    onUvPanChange(state[stateKey.value].uv)
    onZoomChange(state[stateKey.value].zoomFactor)
  })

  return {
    updateThreeDAnnotations(key, value) {
      state.model[key] = value
    },
    updateImageHotspotAnnotations(key, value) {
      state.image[key] = value
    },
    updatePdfHotspotAnnotations(key, value) {
      state.pdf[key] = value
    },
    updateSiteAnnotations(key, value) {
      state.site[key] = value
    },
  }
}
